import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp, findProjectById, getAllTasks } from '../context/AppContext';
import AppLayout from '../components/layout/AppLayout';
import KanbanBoard from '../components/kanban/KanbanBoard';
import NotesPanel from '../components/notes/NotesPanel';
import { Button, Modal, Input, Textarea, Select } from '../components/ui';
import { STATUSES, PRIORITIES, PROJECT_COLORS } from '../data/constants';

/* ── AddTaskModal ─────────────────────────────────────── */
function AddTaskModal({ onClose, onAdd, projectColor }) {
  const [title,    setTitle]    = useState('');
  const [desc,     setDesc]     = useState('');
  const [status,   setStatus]   = useState('todo');
  const [priority, setPriority] = useState('medium');
  const [error,    setError]    = useState('');

  const handleAdd = () => {
    if (!title.trim()) { setError('Task title is required.'); return; }
    onAdd({ title: title.trim(), desc: desc.trim(), status, priority });
    onClose();
  };

  return (
    <Modal title="Add Task" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <Input
          label="Task title"
          required
          value={title}
          onChange={e => { setTitle(e.target.value); setError(''); }}
          placeholder="What needs to be done?"
        />
        <Textarea
          label="Description"
          value={desc}
          onChange={e => setDesc(e.target.value)}
          placeholder="Additional details…"
          rows={2}
        />
        <div className="form-grid-2">
          <Select
            label="Status"
            value={status}
            onChange={e => setStatus(e.target.value)}
            options={STATUSES.map(s => ({ value: s.id, label: s.label }))}
          />
          <Select
            label="Priority"
            value={priority}
            onChange={e => setPriority(e.target.value)}
            options={Object.entries(PRIORITIES).map(([k, v]) => ({ value: k, label: v.label }))}
          />
        </div>
        {error && (
          <p style={{ fontSize: 13, color: '#ef4444', background: '#fee2e2', padding: '8px 12px', borderRadius: 8 }}>{error}</p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleAdd} style={{ background: projectColor, boxShadow: `0 2px 8px ${projectColor}44` }}>
            Add task
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ── EditProjectModal ───────────────────────────────────── */
function EditProjectModal({ project, isSubproject, onClose, onSave }) {
  const [title, setTitle] = useState(project.title);
  const [desc, setDesc] = useState(project.description || '');
  const [color, setColor] = useState(project.color);
  const [stagingUrl, setStagingUrl] = useState(project.stagingUrl || '');
  const [productionUrl, setProductionUrl] = useState(project.productionUrl || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Project name is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: desc.trim(),
        color,
        stagingUrl: stagingUrl.trim(),
        productionUrl: productionUrl.trim(),
      });
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not save project.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isSubproject ? 'Edit Subproject' : 'Edit Project'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <Input
          label={isSubproject ? 'Subproject name' : 'Project name'}
          required
          value={title}
          onChange={e => { setTitle(e.target.value); setError(''); }}
        />
        <Textarea
          label="Description"
          value={desc}
          onChange={e => setDesc(e.target.value)}
          rows={2}
        />
        <Input
          label="Staging URL"
          value={stagingUrl}
          onChange={e => setStagingUrl(e.target.value)}
          placeholder="https://staging.example.com"
          type="url"
        />
        <Input
          label="Production URL"
          value={productionUrl}
          onChange={e => setProductionUrl(e.target.value)}
          placeholder="https://example.com"
          type="url"
        />
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 10 }}>
            Color
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {PROJECT_COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                style={{
                  width: 32, height: 32, borderRadius: '50%', background: c, border: 'none',
                  cursor: 'pointer', transition: 'all 0.15s',
                  outline: color === c ? `3px solid ${c}` : 'none',
                  outlineOffset: 2, transform: color === c ? 'scale(1.15)' : 'none',
                }}
              />
            ))}
          </div>
        </div>
        {error && (
          <p style={{ fontSize: 13, color: '#ef4444', background: '#fee2e2', padding: '8px 12px', borderRadius: 8 }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving} style={{ background: color, boxShadow: `0 2px 8px ${color}44` }}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ── CreateSubprojectModal ────────────────────────────── */
function CreateSubprojectModal({ onClose, onCreate, parentColor }) {
  const [title, setTitle] = useState('');
  const [desc, setDesc]   = useState('');
  const [error, setError] = useState('');

  const handleCreate = () => {
    if (!title.trim()) { setError('Subproject name is required.'); return; }
    onCreate({ title: title.trim(), description: desc.trim(), color: parentColor });
    onClose();
  };

  return (
    <Modal title="New Subproject" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <Input
          label="Subproject name"
          required
          value={title}
          onChange={e => { setTitle(e.target.value); setError(''); }}
          placeholder="e.g. Posts, CRM, Website Builder"
        />
        <Textarea
          label="Description"
          value={desc}
          onChange={e => setDesc(e.target.value)}
          placeholder="What is this subproject about?"
          rows={2}
        />
        {error && (
          <p style={{ fontSize: 13, color: '#ef4444', background: '#fee2e2', padding: '8px 12px', borderRadius: 8 }}>{error}</p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} style={{ background: parentColor, boxShadow: `0 2px 8px ${parentColor}44` }}>
            Create subproject
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ── SubprojectCard ───────────────────────────────────── */
function SubprojectCard({ subproject, parentColor, onClick, onDelete }) {
  const [hover, setHover] = useState(false);
  const done  = subproject.tasks.filter(t => t.status === 'done').length;
  const total = subproject.tasks.length;
  const pct   = total ? Math.round((done / total) * 100) : 0;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: '#fff', borderRadius: 16, padding: '1.25rem',
        border: hover ? `1.5px solid ${parentColor}55` : '1.5px solid #f1f5f9',
        cursor: 'pointer', transition: 'all 0.2s',
        boxShadow: hover ? `0 8px 24px rgba(0,0,0,0.08)` : '0 1px 3px rgba(0,0,0,0.04)',
        transform: hover ? 'translateY(-2px)' : 'none',
        position: 'relative',
      }}
    >
      <button
        onClick={e => { e.stopPropagation(); onDelete(); }}
        style={{
          position: 'absolute', top: 10, right: 10,
          width: 24, height: 24, borderRadius: 7, border: '1.5px solid #fee2e2',
          background: '#fff', color: '#fca5a5', fontSize: 13,
          display: hover ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center',
        }}
      >×</button>

      <h3 style={{ fontSize: 15, fontWeight: 800, color: '#0f0f1a', marginBottom: 4, paddingRight: 20 }}>
        {subproject.title}
      </h3>
      <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: '1rem', lineHeight: 1.4 }}>
        {subproject.description || 'No description'}
      </p>

      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{total} task{total !== 1 ? 's' : ''}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: parentColor }}>{pct}%</span>
        </div>
        <div style={{ height: 4, background: '#f1f5f9', borderRadius: 99 }}>
          <div style={{
            height: '100%', width: `${pct}%`, borderRadius: 99,
            background: parentColor, transition: 'width 0.6s ease',
          }} />
        </div>
      </div>
    </div>
  );
}

/* ── Tab button ───────────────────────────────────────── */
function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '9px 20px', borderRadius: 10, border: 'none',
        background: active ? '#fff' : 'transparent',
        color: active ? '#0f0f1a' : '#94a3b8',
        fontWeight: active ? 700 : 500, fontSize: 14,
        boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
        transition: 'all 0.15s', cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/* ── ProjectPage ──────────────────────────────────────── */
export default function ProjectPage() {
  const { projectId } = useParams();
  const navigate      = useNavigate();
  const { projects, addProject, deleteProject, updateProject, updateTask, deleteTask, addTask, addNote, deleteNote, markProjectSeen } = useApp();
  const [activeTab, setActiveTab]       = useState('board');
  const [showAddTask, setShowAddTask]   = useState(false);
  const [showAddSub, setShowAddSub]     = useState(false);
  const [showEdit, setShowEdit]         = useState(false);

  const project = findProjectById(projects, projectId);

  // Opening a project clears its "new tasks" notification badge.
  useEffect(() => {
    if (project) markProjectSeen(projectId);
  }, [projectId, project, markProjectSeen]);
  const parent  = project?.parentId ? projects.find(p => p.id === project.parentId) : null;
  const isSubproject = !!project?.parentId;
  const isRoot = !isSubproject;
  const hasSubprojects = (project?.children?.length ?? 0) > 0;

  if (!project) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
          <p style={{ fontSize: 18, color: '#64748b', marginBottom: '1rem' }}>Project not found.</p>
          <Button variant="secondary" onClick={() => navigate('/dashboard')}>← Back to Dashboard</Button>
        </div>
      </AppLayout>
    );
  }

  const statsTasks = isRoot && hasSubprojects ? getAllTasks(project) : project.tasks;
  const done  = statsTasks.filter(t => t.status === 'done').length;
  const total = statsTasks.length;
  const pct   = total ? Math.round((done / total) * 100) : 0;

  const backLabel = isSubproject && parent ? `Back to ${parent.title}` : 'Back to Dashboard';
  const backPath  = isSubproject && parent ? `/project/${parent.id}` : '/dashboard';

  const handleCreateSubproject = async (data) => {
    try {
      const id = await addProject({ ...data, parentId: projectId });
      navigate(`/project/${id}`);
    } catch (e) {
      window.alert(e?.message || 'Could not create subproject.');
    }
  };

  return (
    <AppLayout>
      <div className="page-content page-content--fluid" style={{ animation: 'fadeIn 0.25s ease' }}>
        <button
          onClick={() => navigate(backPath)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#64748b', fontSize: 14, marginBottom: '1.5rem', padding: 0,
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#374151'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          {backLabel}
        </button>

        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          marginBottom: '2rem', gap: '1.5rem', flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: project.color + '1a', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ width: 20, height: 20, borderRadius: 6, background: project.color }} />
              </div>
              <div>
                {isSubproject && parent && (
                  <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 2, fontWeight: 600 }}>{parent.title}</p>
                )}
                <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f0f1a', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
                  {project.title}
                </h1>
                <p style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>{project.description || 'No description'}</p>
                {(project.stagingUrl || project.productionUrl) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: 8 }}>
                    {project.stagingUrl && (
                      <a href={project.stagingUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: project.color, fontWeight: 600 }}>
                        Staging ↗
                      </a>
                    )}
                    {project.productionUrl && (
                      <a href={project.productionUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: project.color, fontWeight: 600 }}>
                        Production ↗
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {[
                { label: 'Total',    value: total },
                { label: 'Done',     value: done },
                { label: 'Progress', value: pct + '%' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: project.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, fontWeight: 600, letterSpacing: '0.04em' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <Button variant="secondary" onClick={() => setShowEdit(true)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </Button>
            {isRoot && (
              <Button
                variant="secondary"
                onClick={() => setShowAddSub(true)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add Subproject
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => setShowAddTask(true)}
              style={{ background: project.color, boxShadow: `0 4px 14px ${project.color}44` }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
              Add Task
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: '1.75rem' }}>
          <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99 }}>
            <div style={{
              height: '100%', width: `${pct}%`, borderRadius: 99,
              background: `linear-gradient(90deg, ${project.color}, ${project.color}99)`,
              transition: 'width 0.6s ease',
            }} />
          </div>
        </div>

        {isRoot && (
          <div style={{ marginBottom: '2.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f0f1a' }}>
                Subprojects
                {hasSubprojects && (
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', marginLeft: 8 }}>
                    ({project.children.length})
                  </span>
                )}
              </h2>
              {!hasSubprojects && (
                <Button variant="secondary" size="sm" onClick={() => setShowAddSub(true)}>
                  + Add first subproject
                </Button>
              )}
            </div>
            {hasSubprojects ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
                {project.children.map(sub => (
                  <SubprojectCard
                    key={sub.id}
                    subproject={sub}
                    parentColor={project.color}
                    onClick={() => navigate(`/project/${sub.id}`)}
                    onDelete={() => deleteProject(sub.id)}
                  />
                ))}
              </div>
            ) : (
              <div style={{
                background: '#fff', borderRadius: 16, padding: '2rem',
                border: '1.5px dashed #e2e8f0', textAlign: 'center',
              }}>
                <p style={{ fontSize: 14, color: '#64748b', marginBottom: '1rem' }}>
                  Split this project into subprojects — e.g. Posts, CRM, Website Builder.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => setShowAddSub(true)}
                >
                  Create subproject
                </Button>
              </div>
            )}
          </div>
        )}

        {hasSubprojects && (
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f0f1a', marginBottom: '1rem' }}>
            Project Tasks
          </h2>
        )}

        <div style={{
          display: 'flex', gap: 4, marginBottom: '1.5rem',
          background: '#f1f5f9', borderRadius: 12, padding: 4, width: 'fit-content',
        }}>
          <TabButton active={activeTab === 'board'} onClick={() => setActiveTab('board')}>
            📋 Board
          </TabButton>
          <TabButton active={activeTab === 'notes'} onClick={() => setActiveTab('notes')}>
            📝 Notes {project.notes.length > 0 && `(${project.notes.length})`}
          </TabButton>
        </div>

        {activeTab === 'board' && (
          <KanbanBoard
            tasks={project.tasks}
            projectColor={project.color}
            onTaskUpdate={(taskId, changes) => updateTask(projectId, taskId, changes)}
            onTaskDelete={(taskId) => deleteTask(projectId, taskId)}
            onAddTask={(taskData) => addTask(projectId, taskData)}
          />
        )}
        {activeTab === 'notes' && (
          <NotesPanel
            notes={project.notes}
            onAdd={(text) => addNote(projectId, text)}
            onDelete={(noteId) => deleteNote(projectId, noteId)}
          />
        )}
      </div>

      {showAddTask && (
        <AddTaskModal
          onClose={() => setShowAddTask(false)}
          onAdd={(taskData) => addTask(projectId, taskData)}
          projectColor={project.color}
        />
      )}

      {showAddSub && (
        <CreateSubprojectModal
          onClose={() => setShowAddSub(false)}
          onCreate={handleCreateSubproject}
          parentColor={project.color}
        />
      )}

      {showEdit && (
        <EditProjectModal
          project={project}
          isSubproject={isSubproject}
          onClose={() => setShowEdit(false)}
          onSave={changes => updateProject(projectId, changes)}
        />
      )}
    </AppLayout>
  );
}
